import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';

/** Lightweight type-to-filter select — no external combobox dependency,
 * matches the plain-input styling used across the rest of the app.
 *
 * Rendering is windowed (only the rows inside the scroll viewport are mounted),
 * mirroring the PrimeNG `[virtualScroll]` the Angular screens use on their big
 * lists — cheque reference numbers, PAN numbers and employee codes run to
 * thousands of rows, and mounting them all is what made those dropdowns crawl.
 */

const ITEM_HEIGHT = 34;      // must match the fixed row height below
const LIST_MAX_HEIGHT = 238; // ~7 rows
const OVERSCAN = 6;

const SEARCH_MAX_LENGTH = 20;

// One wording on every Supporting Tools screen for a typed entry that matches
// nothing. A screen's own emptyMessage still covers a list that is empty before
// anything is typed ("Select a branch first" and the like).
const NO_MATCH_MESSAGE = 'No details found on this entry';

/**
 * What the filter box will accept: letters, digits, hyphen and space, capped at
 * SEARCH_MAX_LENGTH.
 *
 * Filtering is a plain substring match on the option label, so any character a
 * label cannot contain can only ever match nothing — typing one silently empties
 * the list and reads as a broken dropdown. Hyphen and space are in because the
 * labels genuinely use them: every branch name is of the form GACHIBOWLI-CAO.
 *
 * This only constrains what is typed or pasted. The selected label is shown
 * through the same input when the list is closed and is never truncated by it.
 */
function sanitizeQuery(raw) {
  return raw.replace(/[^A-Za-z0-9 \-]/g, '').slice(0, SEARCH_MAX_LENGTH);
}

function sameValue(a, b) {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
}

function SearchableSelect({
  options, value, onChange, getLabel, getValue, placeholder, disabled, error,
  emptyMessage = 'No matches', testId, allowClear = true, menuFixed = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuRect, setMenuRect] = useState(null);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // menuFixed (the default): position the list against the viewport so a
  // parent with overflow hidden/auto cannot clip it — every screen sits in a
  // horizontal scroll pane, and some selects sit inside table grids. Tracks the
  // input while anything scrolls or the window resizes.
  useEffect(() => {
    if (!menuFixed || !open) return undefined;
    const place = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [menuFixed, open]);

  // Labels/values are resolved once per options array. getLabel/getValue are
  // inline arrows at every call site, so they are deliberately not dependencies
  // — including them would rebuild this on every keystroke and undo the win.
  const rows = useMemo(() => {
    const list = Array.isArray(options) ? options : [];
    return list.map((option) => {
      const rawLabel = getLabel(option);
      const label = rawLabel === null || rawLabel === undefined ? '' : String(rawLabel);
      return { option, label, search: label.toLowerCase(), val: getValue(option) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.search.includes(q));
  }, [rows, query]);

  const selected = useMemo(() => {
    if (value === null || value === undefined || value === '') return null;
    return rows.find((r) => sameValue(r.val, value)) || null;
  }, [rows, value]);

  const scrollIntoView = useCallback((index) => {
    const el = listRef.current;
    if (!el) return;
    const top = index * ITEM_HEIGHT;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + ITEM_HEIGHT > el.scrollTop + el.clientHeight) {
      el.scrollTop = top + ITEM_HEIGHT - el.clientHeight;
    }
  }, []);

  useEffect(() => {
    if (open) return;
    setQuery('');
    setScrollTop(0);
  }, [open]);

  // Opening with a value already picked should land on that row, not the top.
  useEffect(() => {
    if (!open) return;
    const idx = selected ? filtered.findIndex((r) => r === selected) : -1;
    const next = idx >= 0 ? idx : 0;
    setActiveIndex(next);
    requestAnimationFrame(() => scrollIntoView(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pick = (row) => {
    onChange(row.val);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const next = Math.min(Math.max(activeIndex + delta, 0), Math.max(filtered.length - 1, 0));
      setActiveIndex(next);
      scrollIntoView(next);
    } else if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        pick(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const total = filtered.length;
  const start = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const end = Math.min(total, Math.ceil((scrollTop + LIST_MAX_HEIGHT) / ITEM_HEIGHT) + OVERSCAN);
  const windowRows = filtered.slice(start, end);
  const showClear = allowClear && !disabled && !!selected;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <MagnifyingGlass size={14} color="#9CA3AF" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
        <input
          ref={inputRef}
          className="input-field"
          style={{
            fontSize: '13px',
            paddingLeft: '30px',
            paddingRight: showClear ? '28px' : undefined,
            borderColor: error ? '#DC2626' : undefined,
          }}
          value={open ? query : (selected ? selected.label : '')}
          onChange={(e) => { setQuery(sanitizeQuery(e.target.value)); setOpen(true); setActiveIndex(0); setScrollTop(0); if (listRef.current) listRef.current.scrollTop = 0; }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={SEARCH_MAX_LENGTH}
          autoComplete="off"
          data-testid={testId}
        />
        {showClear && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}
            style={{
              position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9CA3AF',
              display: 'flex', alignItems: 'center',
            }}
            aria-label="Clear selection"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X size={12} />
          </button>
        )}
      </div>
      {open && !disabled && (
        <div
          ref={listRef}
          onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
          style={{
            ...(menuFixed && menuRect
              ? { position: 'fixed', zIndex: 1000, top: `${menuRect.top}px`, left: `${menuRect.left}px`, width: `${menuRect.width}px` }
              : { position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: '4px' }),
            background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px',
            maxHeight: `${LIST_MAX_HEIGHT}px`, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
          data-testid={`${testId}-dropdown`}
        >
          {total === 0 ? (
            <div style={{ padding: '8px 12px', fontSize: '13px', color: query.trim() ? '#DC2626' : '#9CA3AF' }} data-testid={testId ? `${testId}-empty` : undefined}>
              {query.trim() ? NO_MATCH_MESSAGE : emptyMessage}
            </div>
          ) : (
            <div style={{ height: `${total * ITEM_HEIGHT}px`, position: 'relative' }}>
              <div style={{ position: 'absolute', top: `${start * ITEM_HEIGHT}px`, left: 0, right: 0 }}>
                {windowRows.map((row, i) => {
                  const index = start + i;
                  const isSelected = selected === row;
                  const isActive = index === activeIndex;
                  return (
                    <div
                      key={`${String(row.val)}-${index}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(row)}
                      onMouseEnter={() => setActiveIndex(index)}
                      title={row.label}
                      style={{
                        height: `${ITEM_HEIGHT}px`, lineHeight: `${ITEM_HEIGHT}px`,
                        padding: '0 12px', fontSize: '13px', cursor: 'pointer',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        background: isSelected ? '#EFF6FF' : isActive ? '#F3F4F6' : 'white',
                        color: isSelected ? '#002FA7' : '#111827',
                      }}
                      data-testid={`${testId}-option`}
                    >
                      {row.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
